// app/adhd-autism-assessment-tool/page.tsx
'use client'

import React, { Suspense } from 'react'
import Link from 'next/link'
import { Brain, Users, ArrowRight, Phone, CalendarDays } from 'lucide-react'
import { trackAnalyticsEvent } from '@/lib/analytics'
import { clinicIdentity } from '@/lib/clinic-identity'
import { Card, CardContent, CardHeader, CardFooter } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion'
import AssessmentLauncherSwitch from '@/components/freetool/AssessmentLauncherSwitch'
import CrisisGuidance from '@/components/CrisisGuidance'

/** ---- UI config ---- */
type Tool = 'adhd' | 'asd'
type Color = 'blue' | 'purple'
type CardConfig = {
  key: Tool
  color: Color
  title: string
  subtitle: string
  points: string[]
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
}

const STYLES = {
  blue: {
    panel: 'from-blue-50 to-blue-100 border-blue-200',
    badge: 'bg-blue-600',
    title: 'text-blue-800',
    text: 'text-blue-700',
    bullets: 'text-blue-600',
    cta: 'bg-blue-600 hover:bg-blue-700',
    ring: 'focus-visible:ring-blue-600',
  },
  purple: {
    panel: 'from-purple-50 to-purple-100 border-purple-200',
    badge: 'bg-purple-600',
    title: 'text-purple-800',
    text: 'text-purple-700',
    bullets: 'text-purple-600',
    cta: 'bg-purple-600 hover:bg-purple-700',
    ring: 'focus-visible:ring-purple-600',
  },
} as const

const CARDS: CardConfig[] = [
  {
    key: 'adhd',
    color: 'blue',
    title: 'ADHD Assessment',
    subtitle: 'Attention Deficit Hyperactivity Disorder Assessment',
    points: [
      '18 questions',
      '5 - 10 minutes',
      'Attention & hyperactivity symptoms',
    ],
    Icon: Brain,
  },
  {
    key: 'asd',
    color: 'purple',
    title: 'ASD Assessment',
    subtitle: 'Autism Spectrum Disorder Assessment',
    points: [
      '50 questions',
      '10 - 15 minutes',
      'Social & communication traits',
    ],
    Icon: Users,
  },
]

/** Assessment card */
const AssessmentCard: React.FC<{ cfg: CardConfig }> = ({ cfg }) => {
  const s = STYLES[cfg.color]
  const Icon = cfg.Icon

  return (
    <Card
      className={[
        'h-full flex flex-col border transition-transform',
        'bg-gradient-to-br',
        s.panel,
      ].join(' ')}
    >
      <CardHeader className="flex items-center px-4 py-4 sm:px-6 sm:py-5">
        <div
          className={`${s.badge} p-2.5 sm:p-3 rounded-full`}
          aria-hidden="true"
        >
          <Icon
            className="w-6 h-6 sm:w-8 sm:h-8 text-white"
            aria-hidden="true"
          />
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-2 sm:gap-3 px-4 sm:px-6">
        <h3 className={`text-lg sm:text-xl font-bold ${s.title}`}>
          {cfg.title}
        </h3>
        <p className={`text-sm sm:text-base ${s.text}`}>{cfg.subtitle}</p>
        <ul className={`text-xs sm:text-sm space-y-1 ${s.bullets}`}>
          {cfg.points.map((p) => (
            <li key={p}>• {p}</li>
          ))}
        </ul>
      </CardContent>

      <CardFooter className="mt-auto flex justify-end px-4 pb-4 sm:px-6 sm:pb-5">
        <Button
          asChild
          size="sm"
          className={`${s.cta} ${s.ring} text-xs sm:text-sm focus-visible:ring-2 focus-visible:ring-offset-2`}
        >
          <Link href={`?tool=${cfg.key}`} aria-label={`Start ${cfg.title}`}>
            Start Assessment
            <ArrowRight
              className="ml-1.5 sm:ml-2 w-4 h-4 sm:w-5 sm:h-5"
              aria-hidden="true"
            />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  )
}

// Public clinic numbers from the central identity module (landline first,
// then the primary call number) — do not hard-code numbers here.
const PHONE_CONTACTS = [
  clinicIdentity.contact.landline,
  clinicIdentity.contact.phone,
]

/** ---- JSON-LD helpers ---- */
const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || ''
const pagePath = '/adhd-autism-assessment-tool'
const pageUrl = `${siteUrl.replace(/\/$/, '')}${pagePath}`

const JsonLd: React.FC<{ data: Record<string, any> }> = ({ data }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
  />
)

const breadcrumbLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
    { '@type': 'ListItem', position: 2, name: 'Assessments', item: pageUrl },
  ],
}

const faqLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Are these tools a diagnosis?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. They are screening tools only. A qualified clinician must confirm any diagnosis.',
      },
    },
    {
      '@type': 'Question',
      name: 'How long do the assessments take?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'ADHD (ASRS) takes about 5–10 minutes. Autism (AQ) takes about 10–15 minutes.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is my information private?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Results are shown instantly on your device. You can optionally share details to request a clinician review.',
      },
    },
  ],
}

/** ---- Page component ---- */
const ADHDAutismAssessmentTool: React.FC = () => {
  return (
    <>
      <Suspense fallback={null}>
        <AssessmentLauncherSwitch>
          <section className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col items-center pt-24 sm:pt-28 md:pt-32 pb-8 px-3 sm:px-4 md:px-6">
            <div className="w-full max-w-7xl mx-auto flex flex-col gap-4 sm:gap-6">
              <Card className="border border-gray-100 shadow-2xl rounded-xl sm:rounded-2xl">
                <CardContent className="p-4 sm:p-6 md:p-8 flex flex-col gap-4 sm:gap-6">
                  {/* header */}
                  <div className="flex flex-col items-center gap-2 sm:gap-3">
                    <div
                      className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 sm:p-4 rounded-full"
                      aria-hidden="true"
                    >
                      <Brain
                        className="w-8 h-8 sm:w-12 sm:h-12 text-white"
                        aria-hidden="true"
                      />
                    </div>
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center text-balance tracking-tight leading-tight">
                      {clinicIdentity.name} Assessment Tools
                    </h1>
                    <h2 className="text-sm sm:text-base md:text-xl text-gray-600 text-center">
                      Choose an assessment tool to get started
                    </h2>
                  </div>

                  {/* cards row */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-6">
                    {CARDS.map((cfg) => (
                      <div
                        key={cfg.key}
                        className="flex-1 min-w-0 sm:min-w-[240px] md:min-w-[260px]"
                      >
                        <AssessmentCard cfg={cfg} />
                      </div>
                    ))}
                  </div>

                  {/* notice */}
                  <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-3 bg-yellow-50 border-l-4 border-yellow-400 p-3 sm:p-4 rounded">
                    <strong className="text-yellow-800 text-sm sm:text-base shrink-0">
                      Important:
                    </strong>
                    <p className="text-xs sm:text-sm md:text-base text-yellow-700">
                      These assessments are for informational purposes only.
                      Only qualified clinicians can diagnose.
                    </p>
                  </div>

                  {/* contacts */}
                  <div className="bg-blue-50 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 flex flex-col gap-2 sm:gap-3">
                    <h2 className="text-lg sm:text-xl font-semibold text-blue-800 text-center">
                      Ready for a Professional Evaluation?
                    </h2>
                    <p className="text-blue-700 text-center text-xs sm:text-sm md:text-base">
                      For professional evaluation and comprehensive assessment,
                      contact our clinic:
                    </p>
                    {/* Primary next-action: full-width booking CTA in the
                        page's blue/purple language, above the phone actions.
                        Plain semantic link — /book carries the booking
                        funnel, no analytics event here. */}
                    <Button
                      asChild
                      className="w-full gap-2 text-sm sm:text-base py-5 sm:py-6 rounded-md text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus-visible:ring-blue-600"
                    >
                      <Link href="/book">
                        <CalendarDays
                          className="w-4 h-4 sm:w-5 sm:h-5"
                          aria-hidden="true"
                        />
                        Book an Appointment
                      </Link>
                    </Button>
                    <div className="flex flex-col xs:flex-row flex-wrap gap-2 justify-center">
                      {PHONE_CONTACTS.map((num) => {
                        return (
                          <Button
                            key={num.display}
                            asChild
                            size="sm"
                            className="gap-2 text-xs sm:text-sm"
                          >
                            <a
                              href={num.href}
                              aria-label={`Call ${num.display}`}
                              onClick={() =>
                                trackAnalyticsEvent('phone_click', {
                                  cta_location: 'assessment_tool',
                                })
                              }
                            >
                              <Phone
                                className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                                aria-hidden="true"
                              />
                              {num.display}
                            </a>
                          </Button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Urgent-help guidance (P2.13, approved wording): separate
                      from the clinic evaluation/booking block above, and
                      contextual only — never triggered by assessment scores
                      (these tools do not screen for crisis). */}
                  <CrisisGuidance variant="assessment" />

                  {/* SEO content blocks */}
                  <div
                    className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-6"
                    style={{
                      contentVisibility: 'auto',
                      containIntrinsicSize: '600px',
                    }}
                  >
                    <Card
                      className="p-4 sm:p-6 flex-1"
                      style={{
                        contentVisibility: 'auto',
                        containIntrinsicSize: '700px',
                      }}
                    >
                      <h2 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">
                        About the Assessments
                      </h2>
                      <p className="text-xs sm:text-sm text-gray-700">
                        The ADHD tool uses the Adult ADHD Self-Report Scale
                        (ASRS); the Autism tool uses the Autism Spectrum
                        Quotient (AQ). These widely used screeners help identify
                        traits and guide next steps with a clinician.
                      </p>
                    </Card>
                    <Card
                      className="p-4 sm:p-6 flex-1"
                      style={{
                        contentVisibility: 'auto',
                        containIntrinsicSize: '700px',
                      }}
                    >
                      <h2 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">
                        How It Works
                      </h2>
                      <ul className="text-xs sm:text-sm text-gray-700 list-disc pl-4 sm:pl-5 space-y-1">
                        <li>
                          Pick ADHD or Autism and answer simple questions.
                        </li>
                        <li>See your results instantly on screen.</li>
                        <li>
                          Optionally share your details to request a review.
                        </li>
                      </ul>
                    </Card>
                  </div>

                  <Card className="p-4 sm:p-6">
                    <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">
                      Frequently Asked Questions
                    </h2>
                    <Accordion
                      type="single"
                      collapsible
                      className="flex flex-col"
                    >
                      <AccordionItem value="q1">
                        <AccordionTrigger className="text-left text-sm sm:text-base">
                          Are these tools a diagnosis?
                        </AccordionTrigger>
                        <AccordionContent className="text-xs sm:text-sm">
                          No. They are screening tools only. A qualified
                          clinician must confirm any diagnosis.
                        </AccordionContent>
                      </AccordionItem>
                      <AccordionItem value="q2">
                        <AccordionTrigger className="text-left text-sm sm:text-base">
                          How long do the assessments take?
                        </AccordionTrigger>
                        <AccordionContent className="text-xs sm:text-sm">
                          ADHD (ASRS) takes about 5–10 minutes. Autism (AQ)
                          takes about 10–15 minutes.
                        </AccordionContent>
                      </AccordionItem>
                      <AccordionItem value="q3">
                        <AccordionTrigger className="text-left text-sm sm:text-base">
                          Is my information private?
                        </AccordionTrigger>
                        <AccordionContent className="text-xs sm:text-sm">
                          Yes. Results are shown instantly on your device. You
                          can optionally share details to request a clinician
                          review.
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </Card>
                </CardContent>
              </Card>
            </div>

            {/* JSON-LD */}
            <JsonLd data={breadcrumbLd} />
            <JsonLd data={faqLd} />
          </section>
        </AssessmentLauncherSwitch>
      </Suspense>
    </>
  )
}

export default ADHDAutismAssessmentTool
